import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { OptionGroup } from "@/components/OptionGroup";
import { CMYK_FORMATS, FORMAT_LABEL } from "@/lib/tools";
import { downloadFile, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";

const ConfirmDownload = ({ open, setOpen, credits, onConfirm }) => {
  const { t } = useLang();
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="download-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("dl.confirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("dl.confirmText", { n: credits })}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="download-confirm-cancel">{t("dl.cancel")}</AlertDialogCancel>
          <AlertDialogAction data-testid="download-confirm-button" onClick={onConfirm} className="bg-slate-900">{t("dl.confirmBtn")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const DownloadPanel = ({ job, tool, page, engine, onDownloaded }) => {
  const { t, tErr, href } = useLang();
  const { user } = useAuth();
  const [format, setFormat] = useState(tool.defaultFormat);
  const [color, setColor] = useState(tool.color_modes ? "cmyk" : "rgb");
  const [dpi, setDpi] = useState(300);
  const [allPages, setAllPages] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const cmykAllowed = tool.color_modes && CMYK_FORMATS.includes(format);
  const unlimited = !!user?.unlimited;
  const free = unlimited || !!job.unlocked;
  const noCredits = !free && (user?.credits ?? 0) < 1;

  useEffect(() => { if (!cmykAllowed && color === "cmyk") setColor("rgb"); }, [cmykAllowed, color]);

  const params = { format };
  if (tool.color_modes) params.color = color;
  if (tool.dpis) params.dpi = dpi;
  if (tool.key === "pdf" && !allPages) params.page = page;
  const isVector = tool.key === "vectorize";
  if (isVector && engine) params.engine = engine;

  const run = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      await downloadFile(job.job_id, params);
      if (!unlimited) toast.success(free ? t("dl.doneFree") : t("dl.done"));
      onDownloaded?.();
    } catch (e) { toast.error(tErr(errMsg(e))); onDownloaded?.(); }
    finally { setBusy(false); }
  };

  const fmtOptions = tool.formats.map((f) => ({ value: f, label: FORMAT_LABEL[f], hint: isVector ? t(`formatHint.${f}`) : undefined }));

  return (
    <div data-testid="download-panel" className="space-y-6">
      {isVector && engine && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="download-engine-note">{t("vec.downloading", { e: t(`vec.${engine}`) })}</p>}
      <OptionGroup label={t("dl.format")} options={fmtOptions} value={format} onChange={setFormat} testPrefix="download-format" cols={isVector ? 2 : 3} />
      {tool.color_modes && (
        <OptionGroup label={t("dl.color")} cols={2} value={color} onChange={setColor} testPrefix="download-color"
          options={[
            { value: "rgb", label: "RGB", hint: t("dl.rgbHint") },
            { value: "cmyk", label: "CMYK", hint: cmykAllowed ? t("dl.cmykHint") : t("dl.cmykOnly"), disabled: !cmykAllowed },
          ]} />
      )}
      {tool.dpis && (
        <OptionGroup label={t("dl.resolution")} value={dpi} onChange={setDpi} testPrefix="download-dpi"
          options={tool.dpis.map((d) => ({ value: d, label: `${d} DPI`, hint: t(`dl.dpiHints.${d}`) }))} />
      )}
      {tool.key === "pdf" && (
        <OptionGroup label={t("dl.pages")} cols={2} value={allPages} onChange={setAllPages} testPrefix="download-pages"
          options={[{ value: true, label: t("dl.all", { n: job.pages }), hint: t("dl.zip") }, { value: false, label: t("dl.onlyPage", { n: page + 1 }), hint: t("dl.single") }]} />
      )}
      {tool.key === "remove_bg" && format === "jpg" && (
        <p className="flex gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800"><Info className="h-4 w-4 shrink-0" />{t("dl.jpgNoAlpha")}</p>
      )}
      {isVector && (
        <p className="flex gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600"><Info className="h-4 w-4 shrink-0" />{t("dl.vectorTip")}</p>
      )}
      {noCredits ? (
        <div className="space-y-3" data-testid="download-no-credits">
          <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">{t("dl.noCredits")}</p>
          <Button asChild className="h-12 w-full rounded-full bg-[#E40046] text-base hover:bg-[#c3003c]" data-testid="download-buy-credits"><Link to={href("pricing")}>{t("dl.buy")}</Link></Button>
        </div>
      ) : (
        <Button onClick={() => (free ? run() : setConfirm(true))} disabled={busy} className="h-12 w-full rounded-full bg-slate-900 text-base hover:bg-slate-800" data-testid="download-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          {busy ? t("dl.downloading") : `${t("dl.download")} ${FORMAT_LABEL[format]}${tool.color_modes ? ` · ${color.toUpperCase()}` : ""}`}
        </Button>
      )}
      <p className={`text-center text-xs ${free && !unlimited ? "font-medium text-emerald-600" : "text-slate-400"}`} data-testid="download-cost-note">
        {unlimited ? t("dl.freeUnlimited") : free ? t("dl.freeUnlocked") : t("dl.free")}
      </p>
      <ConfirmDownload open={confirm} setOpen={setConfirm} credits={user?.credits ?? 0} onConfirm={run} />
    </div>
  );
};
